import { makeObservable, observable, runInAction } from 'mobx'
import {
  batchDeleteSectionAssignmentConversation,
  createSectionAssignmentConversation,
  getSectionAssignmentConversationsStream,
} from '../firestore/SectionAssignmentConversation'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { SectionAssignmentConversation } from '../models/SectionAssignmentConversation'
import { Cubit } from './core'
import type { StaticModelCollection } from '../firestore-mobx/model'
import { writeBatch } from 'firebase/firestore'
import {
  batchDeleteSectionAssignmentConversationItem,
  createSectionAssignmentConversationItem,
  fetchSectionAssignmentConversationItems,
} from '../firestore/SectionAssignmentConversationItem'

export class InstructorAssignmentConversationsCubit extends Cubit {
  repository: FirebaseRepository
  sectionId: string
  assignmentId: string
  conversations: StaticModelCollection<SectionAssignmentConversation>
  deletingConversations = observable.set<string>([])

  constructor(
    repository: FirebaseRepository,
    params: {
      sectionId: string
      assignmentId: string
    }
  ) {
    super()
    makeObservable(this)

    this.repository = repository
    this.sectionId = params.sectionId
    this.assignmentId = params.assignmentId

    this.conversations =
      SectionAssignmentConversation.emptyCollection(repository)
  }

  initialize(): void {
    if (!this.repository.featureFlags.data.showInstructorConversationsTab) {
      return
    }

    this.addStream(
      getSectionAssignmentConversationsStream(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (conversations) => {
        this.conversations.replaceModels(conversations)
      },
      { name: 'assignment-conversations' }
    )
  }

  startNewConservation() {
    return createSectionAssignmentConversation(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      userId: this.repository.uid,
    })
  }

  async startNewConversationWithMessage(message: string) {
    const conversationId = await this.startNewConservation()
    await createSectionAssignmentConversationItem(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      conversationId,
      userId: this.repository.uid,
      role: 'user',
      userContent: message,
    })
    return conversationId
  }

  async deleteConversation(conversationId: string) {
    if (this.deletingConversations.has(conversationId)) return
    runInAction(() => this.deletingConversations.add(conversationId))

    // create write batch
    const batchLimit = 450

    let currentBatch = writeBatch(this.repository.firestore)
    let batchOperationCount = 0

    // add conversation delete to the batch
    batchDeleteSectionAssignmentConversation(this.repository, {
      batch: currentBatch,
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      conversationId,
    })
    batchOperationCount++

    try {
      // get all conversation items for the conversation
      const conversationItems = await fetchSectionAssignmentConversationItems(
        this.repository,
        {
          sectionId: this.sectionId,
          assignmentId: this.assignmentId,
          conversationId,
        }
      )

      // add delete for all conversation items to the batch
      for (const item of conversationItems) {
        batchDeleteSectionAssignmentConversationItem(this.repository, {
          batch: currentBatch,
          sectionId: this.sectionId,
          assignmentId: this.assignmentId,
          conversationId,
          conversationItemId: item.id,
        })
        batchOperationCount++

        if (batchOperationCount >= batchLimit) {
          await currentBatch.commit()
          currentBatch = writeBatch(this.repository.firestore)
          batchOperationCount = 0
        }
      }

      // commit any remaining operations
      if (batchOperationCount > 0) await currentBatch.commit()
    } finally {
      runInAction(() => this.deletingConversations.delete(conversationId))
    }
  }
}
